// src/stores/useUserStore.js (PATCH v1)
// ─────────────────────────────────────────────────────────────────────────────
// MUDANÇAS:
//   1. state — novo campo `pendingContextSelection` para guardar os dados
//              retornados pelo backend quando há dois contextos.
//   2. _normalizeUser — mapeia `loginContext` vindo do backend.
//   3. login — detecta `requiresContextSelection: true` e armazena o estado
//              pendente em vez de chamar _handleLoginSuccess diretamente.
//   4. confirmLoginContext — NOVO: chama selectLoginContext e finaliza o login
//              com o contexto escolhido pelo usuário.
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from "pinia";
import api from "@/services/api";
import * as userService from "@/services/userService";
import {
  trackSubscriptionCompletedOnce,
  trackFirstActivation,
  toBooleanFlag,
} from "@/utils/tracking";
import { useSubscriptionStore } from "./useSubscriptionStore";
import { usePlanStore } from "./usePlanStore";
// ✅ NOVO: importa selectLoginContext do serviço de contas gerenciadas
import { selectLoginContext } from "@/services/managedAccountService";

import { useManagedAccountStore } from "./useManagedAccountStore";

import { useSubscriptionStatusStore } from "@/stores/useSubscriptionStatusStore";

export const useUserStore = defineStore("user", {
  state: () => ({
    users: [],
    currentUser: null,
    selectedUser: null,
    selectedUserIds: [],

    // >>> NOVO: ids dos usuários que o usuário logado segue. Alimenta a ordem
    // "seguidos primeiro" e o destaque em azul do NoteCreatorsStack.
    // Set (e não array) porque a checagem roda por criador a cada reordenação.
    followedUserIds: new Set(),
    hasLoadedFollowedUserIds: false,
    error: null,
    totalUsersCount: 0,
    totalUsersPages: 1,
    currentUsersPage: 1,
    usersPageSize: 10,
    userSearchTerm: "",
    shouldForceUsersPageOne: false,

    // ✅ NOVO: preenchido quando o backend retorna requiresContextSelection: true.
    // Limpo após confirmLoginContext() ou logout().
    // Shape: { sessionToken: string, contexts: LoginContextDto[], rememberMe: bool } | null
    pendingContextSelection: null,

    // ✅ NOVO: mensagem de expiração de assinatura detectada no login.
    // Preenchida quando o backend retorna ExpiredSubscriptionMessage.
    // Consumida pelo DashboardLayout para exibir o SubscriptionExpiredModal.
    // Zerada após o modal ser exibido.
    subscriptionExpiredMessage: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.currentUser,

    // ✅ NOVO: expõe o saldo de crédito do usuário logado.
    // Pode ser usado como: userStore.creditBalance
    creditBalance: (state) => state.currentUser?.creditBalance ?? 0,

    // ✅ NOVO: true enquanto o usuário ainda não escolheu o contexto
    hasPendingContextSelection: (state) => !!state.pendingContextSelection,

    isAllUsersSelected: (state) => (total) =>
      state.selectedUserIds.length === total,

    activeUsers(state) {
      return state.users.filter((user) => user.status === 1);
    },
  },

  actions: {
    // ── Seguidos ─────────────────────────────────────────────────────────────
    // GET /Follow/following = quem o usuário LOGADO segue (sem parâmetro), o
    // mesmo endpoint já usado por FollowingForm e UserCardModal.
    //
    // Falhando, o Set fica vazio e a ordenação cai para recência pura — a pilha
    // continua funcionando, só perde o critério "seguidos primeiro".
    async fetchFollowedUserIds({ force = false } = {}) {
      if (this.hasLoadedFollowedUserIds && !force) return;

      try {
        const res = await api.get("/Follow/following");
        const list = Array.isArray(res.data) ? res.data : [];

        this.followedUserIds = new Set(
          list
            .map((user) => user.userId)
            .filter((userId) => userId !== null && userId !== undefined)
        );
        this.hasLoadedFollowedUserIds = true;
      } catch {
        this.followedUserIds = new Set();
      }
    },

    // Mantém o Set em dia sem refetch quando o usuário segue/deixa de seguir
    // pelo UserCardModal — que é alcançável de dentro da área de leitura, com a
    // pilha visível.
    setFollowedUser(userId, isFollowing) {
      if (userId === null || userId === undefined) return;

      if (isFollowing) {
        this.followedUserIds.add(userId);
      } else {
        this.followedUserIds.delete(userId);
      }
    },

    // ── Normalização ─────────────────────────────────────────────────────────
    _normalizeUser(user) {
      if (!user) return null;

      const rawStatus = user.status ?? user.userStatus ?? user.Status ?? null;
      let statusInt = 0;

      if (typeof rawStatus === "number") {
        statusInt = rawStatus;
      } else if (typeof rawStatus === "string") {
        const s = rawStatus.trim().toLowerCase();
        if (s === "pending") statusInt = 0;
        else if (s === "active") statusInt = 1;
        else if (s === "inactive") statusInt = 2;
        else if (s === "blocked") statusInt = 3;
        else statusInt = 0;
      } else {
        statusInt = user.active ? 1 : 0;
      }

      // ✅ NOVO: normaliza creditBalance vindo do backend.
      // Aceita camelCase, snake_case e PascalCase.
      // Também cobre o caso de o decimal chegar como string.
      const rawCreditBalance =
        user.creditBalance ?? user.credit_balance ?? user.CreditBalance ?? 0;

      const parsedCreditBalance = Number(rawCreditBalance);

      return {
        ...user,

        userId: user.userId ?? user.user_id ?? user.id ?? user.userID ?? null,
        fullName: user.fullName ?? user.full_name ?? user.name ?? "",
        userName: user.userName ?? user.user_name ?? user.username ?? "",
        email: user.email ?? "",

        status: statusInt,
        statusLabel:
          typeof rawStatus === "string" && rawStatus.trim().length > 0
            ? rawStatus.trim()
            : statusInt === 0
            ? "Pending"
            : statusInt === 1
            ? "Active"
            : statusInt === 2
            ? "Inactive"
            : statusInt === 3
            ? "Blocked"
            : "Pending",

        birthDate: user.birthDate ?? user.birth_date ?? null,
        ddi: user.ddi ?? user.countryCode ?? user.ddi_code ?? "",
        isWhatsappPublic:
          user.isWhatsappPublic ?? user.is_whatsapp_public ?? false,

        telegramUsername:
          user.telegramUsername ??
          user.telegram_username ??
          user.telegram ??
          user.telegramHandle ??
          null,

        country: user.country ?? user.countryName ?? user.ddiName ?? "",
        phoneE164:
          user.phoneE164 ??
          user.phone_e164 ??
          user.phone ??
          user.phone_number ??
          user.phoneNumber ??
          "",

        profession: user.profession ?? "",
        bio: user.bio ?? "",

        photoUrl:
          user.photoUrl ??
          user.photo_url ??
          user.photoPath ??
          user.photo_path ??
          null,

        groups: user.groups ?? user.userGroups ?? user.groupsList ?? [],
        onboardingComplete: user.onboardingComplete ?? false,
        isDeleted: user.isDeleted ?? false,
        deactivatedAt: user.deactivatedAt ?? null,
        purgeAt: user.purgeAt ?? null,

        hasLocalPassword:
          user.hasLocalPassword ?? user.HasLocalPassword ?? null,

        lastIpAddress:
          user.lastIpAddress ??
          user.last_ip_address ??
          user.LastIpAddress ??
          null,

        ipLocation:
          user.ipLocation ?? user.ip_location ?? user.IpLocation ?? null,

        // ✅ NOVO: saldo de crédito disponível na conta do usuário.
        creditBalance: Number.isFinite(parsedCreditBalance)
          ? parsedCreditBalance
          : 0,

        // ✅ NOVO: contexto de login ("owner" | "managed") vindo do backend
        loginContext: user.loginContext ?? user.LoginContext ?? "owner",

        // Marcos de primeira ativação. Normalizados como os demais campos
        // (camelCase / snake_case / PascalCase) porque _handleFirstActivationEvents
        // compara com === e qualquer divergência de grafia mata o evento.
        // Ficam `undefined` quando o backend não informa — nunca `false` por
        // omissão, senão o primeiro refresh de um usuário antigo veria uma
        // transição false→true inexistente e dispararia o evento indevidamente.
        firstNoteCreated: toBooleanFlag(
          user.firstNoteCreated ??
            user.first_note_created ??
            user.FirstNoteCreated,
        ),
        firstTextCreated: toBooleanFlag(
          user.firstTextCreated ??
            user.first_text_created ??
            user.FirstTextCreated,
        ),
        firstActivityCreated: toBooleanFlag(
          user.firstActivityCreated ??
            user.first_activity_created ??
            user.FirstActivityCreated,
        ),
      };
    },

    // ── Login success helper (sem alteração) ─────────────────────────────────
    async _handleLoginSuccess(responseData) {
      const initialUser = this._normalizeUser(responseData.user);

      let managerName = null;
      try {
        const token = responseData.token;
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(
          decodeURIComponent(
            atob(base64)
              .split("")
              .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
              .join(""),
          ),
        );
        managerName = payload["manager_name"] ?? null;
        console.log("[debug] payload manager_name:", payload["manager_name"]);
        console.log("[debug] managerName:", managerName);
      } catch (_) {
        // falha silenciosa — managerName fica null
      }

      console.log("[debug _handleLogin] managerName após decode:", managerName);
      console.log(
        "[debug _handleLogin] token prefix:",
        responseData.token?.substring(0, 20),
      );

      localStorage.setItem("userToken", responseData.token);
      this.currentUser = initialUser;
      this.error = null;

      // ✅ NOVO: armazena mensagem de expiração se o backend a retornou
      this.subscriptionExpiredMessage =
        responseData.expiredSubscriptionMessage ?? null;

      try {
        if (initialUser?.userId) {
          const refreshedResponse = await userService.getUserById(
            initialUser.userId,
          );
          const refreshedUser = this._normalizeUser(refreshedResponse.data);

          this.currentUser = {
            ...refreshedUser,
            loginContext: initialUser.loginContext || "owner",
            planId: initialUser.planId ?? refreshedUser.planId,
            planName: initialUser.planName ?? refreshedUser.planName,
            maxGroupUser:
              initialUser.maxGroupUser ?? refreshedUser.maxGroupUser,
            maxNoteCharacters:
              initialUser.maxNoteCharacters ?? refreshedUser.maxNoteCharacters,
            maxTextCharacters:
              initialUser.maxTextCharacters ?? refreshedUser.maxTextCharacters,
            managerName,
          };

          const managedAccountStore = useManagedAccountStore();
          if (this.currentUser?.loginContext === "managed") {
            await managedAccountStore.fetchMyPermissions();
          } else {
            managedAccountStore.setCurrentMemberPermissions(null);
          }
        } else {
          console.warn("[useUserStore] Login retornou usuário sem userId.");
        }
      } catch (err) {
        console.error(
          "[useUserStore] Falha ao recarregar usuário completo após login:",
          err,
        );
      }
    },

    // ── PATCH: helpers para sincronizar plano e cotas do currentUser ─────────────
    _pickFirstDefined(...values) {
      return values.find((value) => value !== undefined && value !== null);
    },

    _syncCurrentUserPlanFields({
      activeSubscription = null,
      planDetails = null,
      refreshedUser = null,
    } = {}) {
      if (!this.currentUser) return;

      const current = this.currentUser;
      const pick = this._pickFirstDefined;

      const loginContext =
        current.loginContext ?? refreshedUser?.loginContext ?? "owner";

      // No contexto managed, os dados do plano vêm do titular/JWT.
      // Não sobrescrevemos aqui com o plano próprio do usuário membro.
      if (loginContext === "managed") {
        this.currentUser = {
          ...(refreshedUser ?? current),
          loginContext,
          planId: current.planId ?? refreshedUser?.planId ?? null,
          planName: current.planName ?? refreshedUser?.planName ?? null,
          maxGroupUser:
            current.maxGroupUser ?? refreshedUser?.maxGroupUser ?? null,
          maxNoteCharacters:
            current.maxNoteCharacters ??
            refreshedUser?.maxNoteCharacters ??
            null,
          maxTextCharacters:
            current.maxTextCharacters ??
            refreshedUser?.maxTextCharacters ??
            null,
          managerName:
            current.managerName ?? refreshedUser?.managerName ?? null,
        };
        return;
      }

      const planId = pick(
        activeSubscription?.planId,
        planDetails?.planId,
        planDetails?.plan_id,
        refreshedUser?.planId,
        refreshedUser?.plan_id,
        current.planId,
      );

      const planName = pick(
        planDetails?.name,
        planDetails?.planName,
        planDetails?.plan_name,
        activeSubscription?.planName,
        activeSubscription?.plan_name,
        refreshedUser?.planName,
        refreshedUser?.plan_name,
        current.planName,
      );

      const maxGroupUser = pick(
        planDetails?.maxGroupUser,
        planDetails?.max_group_user,
        activeSubscription?.maxGroupUser,
        activeSubscription?.max_group_user,
        refreshedUser?.maxGroupUser,
        refreshedUser?.max_group_user,
        current.maxGroupUser,
      );

      const maxNoteCharacters = pick(
        planDetails?.maxNoteCharacters,
        planDetails?.max_note_characters,
        activeSubscription?.maxNoteCharacters,
        activeSubscription?.max_note_characters,
        refreshedUser?.maxNoteCharacters,
        refreshedUser?.max_note_characters,
        current.maxNoteCharacters,
      );

      const maxTextCharacters = pick(
        planDetails?.maxTextCharacters,
        planDetails?.max_text_characters,
        activeSubscription?.maxTextCharacters,
        activeSubscription?.max_text_characters,
        refreshedUser?.maxTextCharacters,
        refreshedUser?.max_text_characters,
        current.maxTextCharacters,
      );

      this.currentUser = {
        ...(refreshedUser ?? current),
        loginContext,
        planId,
        planName,
        maxGroupUser,
        maxNoteCharacters,
        maxTextCharacters,
        managerName: current.managerName ?? refreshedUser?.managerName ?? null,
      };
    },
    // ── FIM PATCH ───────────────────────────────────────────────────────────────

    async fetchCurrentUserSubscription() {
      const subscriptionStore = useSubscriptionStore();
      const planStore = usePlanStore();
      const managedAccountStore = useManagedAccountStore();

      if (!this.currentUser?.userId) return;

      // No contexto managed, os dados de plano/cotas vêm do titular.
      if (this.currentUser.loginContext === "managed") return;

      try {
        // PATCH: antes só buscava planos se a lista estivesse vazia.
        // Agora recarrega para evitar plano/cotas stale após downgrade/upgrade.
        await planStore.fetchActivePlans();

        await subscriptionStore.fetchSubscriptionsPaged(
          1,
          100,
          this.currentUser.userId,
        );

        const activeSubscription = subscriptionStore.subscriptions.find(
          (sub) => sub.status?.toLowerCase() === "active",
        );

        if (activeSubscription) {
          const planDetails = planStore.plans.find(
            (p) => p.planId === activeSubscription.planId,
          );

          // PATCH: atualiza plano + cotas, não apenas planId/planName.
          this._syncCurrentUserPlanFields({
            activeSubscription,
            planDetails,
          });
        }

        // PATCH: o card "Cota de Contas Membro" usa aggregatedUsage,
        // então também precisa ser recarregado após troca de plano.
        try {
          await managedAccountStore.fetchAggregatedUsage();
        } catch (err) {
          console.warn(
            "[useUserStore] Falha ao recarregar consumo agregado após sincronizar plano:",
            err?.message || err,
          );
        }
      } catch (error) {
        console.error(
          "Falha ao buscar e associar a assinatura do usuário:",
          error,
        );
      }
    },

    // ── ✅ MUDANÇA — login ────────────────────────────────────────────────────
    // Detecta requiresContextSelection e armazena o estado pendente.
    // Se o backend retornar contexto único, comportamento é idêntico ao anterior.
    async login(credentials) {
      try {
        const response = await userService.login(credentials);
        const data = response.data;

        // ✅ NOVO: dois contextos disponíveis — armazena e aguarda escolha do usuário
        if (data?.requiresContextSelection === true) {
          this.pendingContextSelection = {
            sessionToken: data.sessionToken,
            contexts: data.contexts ?? [],
            rememberMe: credentials.rememberMe ?? false,
          };
          this.error = null;
          // Retorna o dado para o componente saber que deve exibir a seleção
          return data;
        }

        // Contexto único — fluxo normal
        await this._handleLoginSuccess(data);
        return data;
      } catch (error) {
        if (
          error.response?.status === 403 &&
          error.response?.data?.requiresReactivation
        ) {
          this.error = "Account Deactivated";
          error.deactivatedAt = error.response.data.deactivatedAt;
          error.requiresReactivation = true;
        } else {
          this.error =
            error.response?.data?.message || "Email ou senha inválidos.";
        }
        console.error("❌ Erro ao fazer login:", this.error);
        throw error;
      }
    },

    // ── ✅ NOVO — confirmLoginContext ─────────────────────────────────────────
    // Chamado pelo componente de seleção de contexto após o usuário escolher.
    // relationshipId = null → owner; número → managed.
    async confirmLoginContext(relationshipId = null) {
      if (!this.pendingContextSelection) {
        throw new Error("Nenhuma seleção de contexto pendente.");
      }

      try {
        const { sessionToken } = this.pendingContextSelection;
        const response = await selectLoginContext(sessionToken, relationshipId);

        // Limpa o estado pendente antes de finalizar o login
        this.pendingContextSelection = null;

        await this._handleLoginSuccess(response.data);
        return response.data;
      } catch (error) {
        const msg =
          error.response?.data?.message || "Erro ao selecionar contexto.";
        this.error = msg;
        console.error("❌ Erro ao confirmar contexto:", msg);
        throw new Error(msg);
      }
    },

    // ── loginWithGoogle ────────────────────────────────────────────────────
    // ✅ MUDANÇA: detecta requiresContextSelection, igual ao login() por senha —
    // uma conta Google pode ser membro gerenciado de um titular (ou ter conta
    // própria + vínculo de membro ao mesmo tempo).
    async loginWithGoogle(idToken) {
      try {
        const response = await userService.loginWithGoogle(idToken);
        const data = response.data;

        if (data?.requiresContextSelection === true) {
          this.pendingContextSelection = {
            sessionToken: data.sessionToken,
            contexts: data.contexts ?? [],
            rememberMe: false,
          };
          this.error = null;
          return data;
        }

        await this._handleLoginSuccess(data);
        return data;
      } catch (error) {
        if (
          error.response?.status === 403 &&
          error.response?.data?.requiresReactivation
        ) {
          this.error = "Account Deactivated";
          error.requiresReactivation = true;
        } else {
          this.error =
            error.response?.data?.message ||
            "Falha na autenticação com Google.";
        }
        throw error;
      }
    },

    // ── exchangeGoogleRedirectCode ─────────────────────────────────────────
    // Fluxo redirect do Google (mobile): o backend já autenticou e guardou o
    // resultado sob um código de uso único. Aqui só resgatamos. O corpo é o
    // mesmo de loginWithGoogle, então o tratamento a seguir é idêntico.
    async exchangeGoogleRedirectCode(code) {
      try {
        const response = await userService.exchangeGoogleRedirectCode(code);
        const data = response.data;

        if (data?.requiresContextSelection === true) {
          this.pendingContextSelection = {
            sessionToken: data.sessionToken,
            contexts: data.contexts ?? [],
            rememberMe: false,
          };
          this.error = null;
          return data;
        }

        await this._handleLoginSuccess(data);
        return data;
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Não foi possível concluir o login com o Google.";
        throw error;
      }
    },

    // ── tryAutoLogin (sem alteração) ─────────────────────────────────────────
    async tryAutoLogin() {
      console.log("tryAutoLogin");
      if (this.currentUser) return true;

      const token = localStorage.getItem("userToken");
      if (!token) return false;

      try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payloadJson = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
        );
        const payload = JSON.parse(payloadJson);

        const expiryTime = payload.exp * 1000;
        if (Date.now() >= expiryTime) {
          this.logout();
          return false;
        }

        const userId =
          payload[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
          ];

        if (!userId) {
          this.logout();
          return false;
        }

        const response = await userService.getUserById(userId);

        if (response.data.userId) {
          const normalized = this._normalizeUser(response.data);

          // Lê contexto e dados de plano direto do JWT
          const loginContext = payload["ctx"] ?? "owner";
          const planId = payload["plan_id"]
            ? parseInt(payload["plan_id"])
            : normalized.planId ?? null;
          const planName = payload["plan_name"]
            ? payload["plan_name"]
            : normalized.planName ?? null;
          const maxNote = payload["max_note"]
            ? parseInt(payload["max_note"])
            : normalized.maxNoteCharacters ?? null;
          const maxText = payload["max_text"]
            ? parseInt(payload["max_text"])
            : normalized.maxTextCharacters ?? null;
          const maxGroup = payload["max_group"]
            ? parseInt(payload["max_group"])
            : normalized.maxGroupUser ?? null;
          // ✅ NOVO: nome do titular vindo do JWT
          const managerName = payload["manager_name"] ?? null;

          console.log(
            "[debug tryAutoLogin] manager_name do payload:",
            payload["manager_name"],
          );
          console.log("[debug tryAutoLogin] managerName:", managerName);

          this.currentUser = {
            ...normalized,
            loginContext,
            planId,
            planName,
            maxNoteCharacters: maxNote,
            maxTextCharacters: maxText,
            maxGroupUser: maxGroup,
            managerName,
          };

          this.error = null;

          // ── NOVO: resolve permissões do vínculo se for membro ────────────
          const managedAccountStore = useManagedAccountStore();
          if (loginContext === "managed") {
            await managedAccountStore.fetchMyPermissions();
          } else {
            managedAccountStore.setCurrentMemberPermissions(null);
          }

          return true;
        }
      } catch (error) {
        console.error("Auto-login falhou:", error);
        this.logout();
        return false;
      }

      return false;
    },

    // ── Restante das actions (sem alteração) ─────────────────────────────────

    async markOnboardingAsComplete() {
      if (!this.currentUser) return;
      try {
        await userService.completeOnboarding();
        this.currentUser = { ...this.currentUser, onboardingComplete: true };
        this.error = null;
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Não foi possível finalizar o onboarding.";
        throw new Error(this.error);
      }
    },

    async registerUser(userData) {
      try {
        const response = await userService.register(userData);
        return response.data;
      } catch (error) {
        // [PATCH] relança o erro original preservando error.response
        this.error = error.response?.data?.message || error.message;
        throw error; // em vez de throw new Error(this.error)
      }
    },

    async forgotPassword(email) {
      try {
        const response = await userService.forgotPassword({ email });
        this.error = null;
        return response.data;
      } catch (error) {
        this.error =
          error.response?.data?.message || "Erro ao solicitar redefinição.";
        throw new Error(this.error);
      }
    },

    async resetPassword(resetData) {
      try {
        const response = await userService.resetPassword(resetData);
        this.error = null;
        return response.data;
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Código inválido ou senha não alterada.";
        throw new Error(this.error);
      }
    },

    async verifyUserEmail(verificationData) {
      try {
        const response = await userService.verifyEmail(verificationData);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    // ── PATCH: reenvio do código de verificação de e-mail (passo 4 do cadastro) ──
    // Lança erro especial { code: 'RATE_LIMIT', retryAfterSeconds, message } quando
    // o backend retorna 429, para que o componente possa ajustar o cooldown local.
    async resendVerificationCode(email) {
      try {
        const response = await userService.resendVerificationCode({ email });
        this.error = null;
        return response.data;
      } catch (error) {
        // Tratamento específico de 429 — rate limit do backend
        if (error.response?.status === 429) {
          // Prefere header Retry-After (padrão HTTP); usa body como fallback
          const headerRetry = error.response.headers?.["retry-after"];
          const bodyRetry = error.response.data?.retryAfterSeconds;
          const retryAfterSeconds =
            Number(headerRetry) || Number(bodyRetry) || 60;

          const message =
            error.response.data?.message ||
            "Aguarde antes de solicitar um novo código.";

          this.error = message;

          // Lança erro estruturado para o componente reagir
          const rateLimitError = new Error(message);
          rateLimitError.code = "RATE_LIMIT";
          rateLimitError.retryAfterSeconds = retryAfterSeconds;
          throw rateLimitError;
        }

        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },
    // ── FIM PATCH ────────────────────────────────────────────────────────────────
    // ── FIM PATCH ────────────────────────────────────────────────────────────────

    async changePassword({ currentPassword, newPassword, confirmNewPassword }) {
      try {
        const response = await userService.changePassword({
          currentPassword,
          newPassword,
          confirmNewPassword,
        });
        this.error = null;
        return response.data;
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.Message ||
          error.response?.data?.errors?.ConfirmNewPassword?.[0] ||
          error.response?.data?.errors?.NewPassword?.[0] ||
          error.message ||
          "Não foi possível alterar a senha.";
        this.error = msg;
        throw new Error(msg);
      }
    },

    async deactivateMyAccount() {
      if (!this.currentUser?.userId) throw new Error("Nenhum usuário logado.");
      try {
        const response = await userService.deactivateMe();
        this.logout();
        return response.data;
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          "Falha ao desativar conta.";
        throw new Error(msg);
      }
    },

    async reactivateAccount(payload = {}) {
      try {
        const response = await userService.reactivateAccount(payload);
        return response.data;
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          "Falha ao reativar conta.";
        throw new Error(msg);
      }
    },

    clearStoreError() {
      this.error = null;
    },

    logout() {
      // ── NOVO: limpa permissões de vínculo ao sair ─────────────────────
      try {
        const managedAccountStore = useManagedAccountStore();
        managedAccountStore.setCurrentMemberPermissions(null);
        managedAccountStore.clearRelatedUsers();
      } catch (_) {
        /* silencioso */
      }
      this.currentUser = null;
      // ✅ Limpa seleção pendente ao sair
      this.pendingContextSelection = null;
      localStorage.removeItem("userToken");

      // Para polling de status da assinatura
      const subscriptionStatusStore = useSubscriptionStatusStore();
      subscriptionStatusStore.stopPolling();
    },

    setSelectedUser(user) {
      this.selectedUser = user;
    },

    toggleUserSelection(id) {
      const index = this.selectedUserIds.indexOf(id);
      if (index > -1) this.selectedUserIds.splice(index, 1);
      else this.selectedUserIds.push(id);
    },

    selectAllUsers(ids) {
      this.selectedUserIds = [...ids];
    },

    clearUserSelection() {
      this.selectedUserIds = [];
    },

    async fetchUsers(pageNumber = 1, pageSize = 10, searchTerm = "") {
      try {
        const response = await userService.fetchUsersPaged(
          pageNumber,
          pageSize,
          searchTerm,
        );
        this.users = (response.data.data || []).map((u) =>
          this._normalizeUser(u),
        );
        this.totalUsersCount = response.data.totalCount;
        this.totalUsersPages = response.data.totalPages;
        this.currentUsersPage = pageNumber;
        this.userSearchTerm = searchTerm;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        this.users = [];
        this.totalUsersCount = 0;
        this.totalUsersPages = 1;
        this.currentUsersPage = 1;
        throw new Error(this.error);
      }
    },

    async getUserById(id) {
      try {
        const response = await userService.getUserById(id);
        const normalized = this._normalizeUser(response.data);
        this.selectedUser = normalized;

        const index = this.users.findIndex(
          (u) => (u.userId ?? u.user_id ?? u.id) === normalized.userId,
        );

        if (index === -1) this.users.push(normalized);
        else this.users[index] = normalized;

        if (this.currentUser && this.currentUser.userId === normalized.userId) {
          const previousUser = this.currentUser;
          const loginContext = previousUser.loginContext ?? "owner";

          if (loginContext === "managed") {
            this.currentUser = {
              ...normalized,
              loginContext,
              planId: previousUser.planId ?? normalized.planId,
              planName: previousUser.planName ?? normalized.planName,
              maxGroupUser:
                previousUser.maxGroupUser ?? normalized.maxGroupUser,
              maxNoteCharacters:
                previousUser.maxNoteCharacters ?? normalized.maxNoteCharacters,
              maxTextCharacters:
                previousUser.maxTextCharacters ?? normalized.maxTextCharacters,
              managerName: previousUser.managerName || null,
            };
          } else {
            this.currentUser = {
              ...previousUser,
              ...normalized,
              loginContext,
              managerName: previousUser.managerName || null,
            };

            // PATCH: atualiza cotas junto com plano.
            this._syncCurrentUserPlanFields({
              refreshedUser: normalized,
            });

            const managedAccountStore = useManagedAccountStore();
            try {
              await managedAccountStore.fetchAggregatedUsage();
            } catch (err) {
              console.warn(
                "[useUserStore] Falha ao recarregar consumo agregado no getUserById:",
                err?.message || err,
              );
            }
          }
        }

        return normalized;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    async createUser(userData) {
      try {
        const response = await userService.createUser(userData);
        this.totalUsersCount++;
        this.totalUsersPages = Math.ceil(
          this.totalUsersCount / this.usersPageSize,
        );
        this.currentUsersPage = this.totalUsersPages;
        await this.fetchUsers(
          this.currentUsersPage,
          this.usersPageSize,
          this.userSearchTerm,
        );
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    async updateUser(userId, userData) {
      try {
        let payload;
        if (userData instanceof FormData) {
          userData.set("userId", String(userId));
          payload = userData;
        } else {
          payload = { ...userData, userId };
        }
        await userService.updateUser(userId, payload);
        await this.fetchUsers(
          this.currentUsersPage,
          this.usersPageSize,
          this.userSearchTerm,
        );
        if (this.currentUser && this.currentUser.userId === userId)
          await this.refreshCurrentUser();
      } catch (error) {
        console.error("❌ Erro ao atualizar usuário:", error);
        throw error;
      }
    },

    async deleteUserById(id) {
      try {
        await userService.deleteUser(id);
        this.users = this.users.filter((user) => user.userId !== id);
        this.totalUsersCount--;
        if (this.users.length === 0 && this.currentUsersPage > 1)
          this.currentUsersPage--;
        await this.fetchUsers(
          this.currentUsersPage,
          this.usersPageSize,
          this.userSearchTerm,
        );
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        if (serverMessage.includes("subscriptions_ibfk"))
          throw new Error(
            "Não é possível excluir o usuário, pois ele já está associado a um plano de assinatura.",
          );
        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    async deleteSelectedUsers() {
      if (!this.selectedUserIds.length) return;
      try {
        await userService.deleteUsersBatch(this.selectedUserIds);
        this.clearUserSelection();
        await this.fetchUsers(
          this.currentUsersPage,
          this.usersPageSize,
          this.userSearchTerm,
        );
        if (this.users.length === 0 && this.currentUsersPage > 1) {
          this.currentUsersPage--;
          await this.fetchUsers(
            this.currentUsersPage,
            this.usersPageSize,
            this.userSearchTerm,
          );
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    setUserSearchTerm(term) {
      this.userSearchTerm = term;
      this.currentUsersPage = 1;
      this.shouldForceUsersPageOne = true;
      this.fetchUsers(1, this.usersPageSize, term);
    },

    async assignUserToGroup(userId, groupId) {
      try {
        const response = await userService.assignUserToGroup(userId, groupId);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    async removeUserFromGroup(userGroupId) {
      try {
        const response = await userService.removeUserFromGroup(userGroupId);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },

    async _handlePaymentConversionPending(user) {
      if (!user?.paymentConversionPending) return;
      try {
        // Ponto ÚNICO de disparo do evento de conversão. A idempotência é
        // garantida pelo backend: a flag `paymentConversionPending` só vem true
        // enquanto a conversão não foi reconhecida; disparamos o evento e em
        // seguida damos o acknowledge, que zera a flag no servidor.
        trackSubscriptionCompletedOnce(user);
        await userService.acknowledgePaymentConversion();
        if (this.currentUser) {
          this.currentUser = { ...this.currentUser, paymentConversionPending: false };
        }
      } catch (err) {
        console.warn("[useUserStore] Falha ao registrar conversão de pagamento:", err?.message || err);
      }
    },

    // Dispara litterae_first_activation UMA ÚNICA VEZ por usuário — no primeiro
    // texto, atividade ou nota, o que vier primeiro. Não é um evento por tipo de
    // recurso: quem já criou um texto e depois cria uma nota não dispara de novo,
    // porque já estava ativado. O `activation_type` diz por qual porta a pessoa
    // ativou, não que ela tenha criado aquele recurso pela primeira vez.
    //
    // A memória são os três booleanos permanentes do backend (first_*_created),
    // entregues tanto no login quanto no getUserById. Daí se deriva tudo:
    //   - algum já era true em previousUser  → usuário já ativado, não dispara;
    //   - senão, a 1ª transição false→true   → dispara uma vez.
    // Como os booleanos nunca voltam a false (mesmo apagando o recurso) e são por
    // usuário, não há re-disparo em outro navegador, aba anônima ou storage limpo.
    //
    // Adoção por recurso ("quantos usuários já criaram nota?") NÃO se mede por
    // este evento — conte as colunas first_*_created direto no banco.
    _handleFirstActivationEvents(previousUser, refreshedUser) {
      if (!refreshedUser) return;
      const transitions = [
        ["firstTextCreated", "first_text_created"],
        ["firstActivityCreated", "first_activity_created"],
        ["firstNoteCreated", "first_note_created"],
      ];

      // Diagnóstico: se o backend não entrega nenhum dos marcos, a transição
      // false→true nunca acontece e litterae_first_activation jamais dispara —
      // uma falha silenciosa que só aparecia inspecionando o dataLayer.
      const hasAnyFlag = transitions.some(
        ([field]) => refreshedUser[field] !== undefined,
      );
      if (!hasAnyFlag) {
        console.warn(
          "[useUserStore] litterae_first_activation inativo: o usuário retornado " +
            "pelo backend não traz firstNoteCreated/firstTextCreated/firstActivityCreated.",
        );
        return;
      }

      // Já ativado por qualquer um dos marcos: nada a fazer.
      const wasAlreadyActivated = transitions.some(
        ([field]) => previousUser?.[field] === true,
      );
      if (wasAlreadyActivated) return;

      // Primeira transição na ordem acima — determinístico quando mais de um
      // marco vira true no mesmo refresh.
      const activated = transitions.find(
        ([field]) =>
          previousUser?.[field] === false && refreshedUser[field] === true,
      );
      if (activated) trackFirstActivation(activated[1]);
    },

    async refreshCurrentUser() {
      if (!this.currentUser?.userId) return;

      const previousUser = this.currentUser;
      const loginContext = previousUser.loginContext ?? "owner";
      const managerName = previousUser.managerName || null;

      try {
        const response = await userService.getUserById(previousUser.userId);
        const refreshed = this._normalizeUser(response.data);

        if (loginContext === "managed") {
          this.currentUser = {
            ...refreshed,
            loginContext,
            planId: previousUser.planId ?? refreshed.planId,
            planName: previousUser.planName ?? refreshed.planName,
            maxGroupUser: previousUser.maxGroupUser ?? refreshed.maxGroupUser,
            maxNoteCharacters:
              previousUser.maxNoteCharacters ?? refreshed.maxNoteCharacters,
            maxTextCharacters:
              previousUser.maxTextCharacters ?? refreshed.maxTextCharacters,
            managerName,
          };
        } else {
          // PATCH: para owner, o backend é a fonte da verdade após downgrade/upgrade.
          // Não preservar cegamente planId/planName/cotas antigas.
          this.currentUser = {
            ...previousUser,
            ...refreshed,
            loginContext,
            managerName,
          };

          this._syncCurrentUserPlanFields({
            refreshedUser: refreshed,
          });
        }

        this.error = null;

        const managedAccountStore = useManagedAccountStore();

        if (this.currentUser?.loginContext === "managed") {
          await managedAccountStore.fetchMyPermissions();
        } else {
          managedAccountStore.setCurrentMemberPermissions(null);

          // PATCH: atualiza também o agregado usado por "Cota de Contas Membro".
          try {
            await managedAccountStore.fetchAggregatedUsage();
          } catch (err) {
            console.warn(
              "[useUserStore] Falha ao recarregar consumo agregado no refreshCurrentUser:",
              err?.message || err,
            );
          }
        }
        await this._handlePaymentConversionPending(this.currentUser);
        this._handleFirstActivationEvents(previousUser, refreshed);
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        throw new Error(this.error);
      }
    },
  },
});
